import { FieldSchema, CCGroupedConfig } from '../types';

/**
 * Config transforms between the form's flat `{fieldName: value}` shape and
 * the wire's grouped-by-field-class shape (`{credentials: {...}, model:
 * {...}, behavioral: {...}}`).
 *
 * The wire contract (Command Center Models API) always groups config by each
 * field's `FieldSchema.group`; the dynamic form (DynamicConfigFields /
 * prepareConfigForSubmit) works in a flat field-name space. These two
 * helpers are the single seam between the two representations, so no caller
 * hand-rolls the grouping and gets a field into the wrong group.
 */

/**
 * Group a flat config into `{group: {fieldName: value}}` using each field's
 * declared `group`. Only fields present in `fields` are included (a stray UI
 * helper key like `model_is_other`, or the `model` field itself when callers
 * pass a model-excluding schema, is dropped), and only groups that end up
 * with at least one value appear — matching the contract's "only include the
 * keys it actually has data for".
 */
export function groupConfigByFieldClass(
  fields: FieldSchema[],
  flatConfig: Record<string, unknown>,
): CCGroupedConfig {
  const groupByName = new Map(fields.map((f) => [f.name, f.group]));
  const grouped: CCGroupedConfig = {};
  for (const [name, value] of Object.entries(flatConfig)) {
    const group = groupByName.get(name);
    if (!group) continue;
    (grouped[group] ??= {})[name] = value;
  }
  return grouped;
}

/**
 * Inverse of {@link groupConfigByFieldClass}: merge every group's fields back
 * into one flat `{fieldName: value}` map for seeding the form. Field names
 * are unique across groups, so the merge is lossless.
 */
export function flattenGroupedConfig(grouped: CCGroupedConfig | undefined | null): Record<string, unknown> {
  const flat: Record<string, unknown> = {};
  for (const groupValues of Object.values(grouped ?? {})) {
    Object.assign(flat, groupValues);
  }
  return flat;
}
